import React from "react";
import {
  Box,
  Typography,
  CardContent,
  Divider,
  LinearProgress,
} from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { SmsOutboxStatsDto } from "../../../shared/dtos/sms/SmsOutboxStatsDto";

interface SmsOutboxWidgetProps {
  data: SmsOutboxStatsDto;
}

const SmsOutboxWidget: React.FC<SmsOutboxWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("SMS_OUTBOX");
  const Icon = meta.icon;

  const isOptimal = data.deliveryRate >= 90;

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Today: {data.totalToday}
            </Typography>
          </Box>
        </Box>

        {/* Delivery Rate Focus */}
        <Box sx={{ my: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Carrier Delivery Rate
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: isOptimal ? "success.main" : "warning.main",
              }}
            >
              {data.deliveryRate}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={data.deliveryRate || 0}
            color={isOptimal ? "success" : "warning"}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Status Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
            textAlign: "center",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "success.main", fontWeight: "bold" }}
            >
              {data.delivered}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Delivered
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "warning.main", fontWeight: "bold" }}
            >
              {data.pending}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Pending
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "error.main", fontWeight: "bold" }}
            >
              {data.failed}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Failed
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default SmsOutboxWidget;
